import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import Slider from '@mui/material/Slider';
import InputNumber from 'common/components/Elements/Form/InputNumber/InputNumber';
import { precisionRound } from 'common/utilities/core/number';
import { stringIsNumber } from 'common/utilities/core/string';
import { ThemeVars } from 'core/utilities/theme';
import React, { useEffect, useRef, useState } from 'react';
import type { SelectedColorScheme } from 'sequenceAnalyser/utilities/colors';

interface ComponentProps {
  colors: SelectedColorScheme;
  onHydrophobicityChange: (value: SelectedColorScheme['hydrophobicity']) => void;
}

function ColorsHydrophobicity({
  colors, onHydrophobicityChange,
}: ComponentProps) {
  const defaultHydrophobicity = useRef(colors.hydrophobicity as [number, number]);
  const [controlledHydrophobicity, setControlledHydrophobicity] = useState([
    defaultHydrophobicity.current[0].toString(),
    defaultHydrophobicity.current[1].toString(),
  ]);
  const hydrophobicValues = Object.values(colors.infos).map((i) => i.hydrophobicValue) as number[];
  const diffs = hydrophobicValues.sort((a, b) => a - b).map((v, i) => (
    hydrophobicValues[i + 1] === undefined ? 1 : hydrophobicValues[i + 1] - v
  )).filter((v) => v !== 0);
  // The slider step is the minimum difference between two hydrophobic values
  const step = precisionRound(Math.min(...diffs), 2);
  const min = Math.min(...hydrophobicValues);
  const max = Math.max(...hydrophobicValues);

  useEffect(() => {
    if (
      stringIsNumber(controlledHydrophobicity[0]) && stringIsNumber(controlledHydrophobicity[1])
      && Number(controlledHydrophobicity[1]) > Number(controlledHydrophobicity[0])
    ) {
      onHydrophobicityChange([
        Number(controlledHydrophobicity[0]),
        Number(controlledHydrophobicity[1]),
      ]);
    }
  }, [controlledHydrophobicity, onHydrophobicityChange]);

  return (
    <Box mt={1.25} mr={2}>
      <InputLabel sx={{ fontSize: ThemeVars.compactFontSize }}>Hydrophobicity threshold</InputLabel>
      <Box sx={{ mt: 1, mb: -2 }}>
        <Slider
          value={colors.hydrophobicity}
          min={min}
          max={max}
          valueLabelDisplay="auto"
          step={step}
          onChange={(e, value) => {
            if (Array.isArray(value) && value.length === 2) {
              setControlledHydrophobicity([value[0].toString(), value[1].toString()]);
            }
          }}
        />
      </Box>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: '35%' }}>
          <InputNumber
            value={controlledHydrophobicity[0]}
            onChange={(value) => {
              if (!colors.hydrophobicity) return;
              setControlledHydrophobicity([value, controlledHydrophobicity[1]]);
            }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ width: '35%', '& input': { textAlign: 'right' } }}>
          <InputNumber
            value={controlledHydrophobicity[1]}
            onChange={(value) => {
              if (!colors.hydrophobicity) return;
              setControlledHydrophobicity([controlledHydrophobicity[0], value]);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default ColorsHydrophobicity;
